import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';

import { ComplexValue } from 'app/types-shared';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { usePrevious } from 'app/infrastructure/hooks';
import { deepEquals } from 'app/infrastructure/js';

import { compareModel } from './utils/compare-model';
import { actionCreators } from './actions';
import {
  selectBreadcrumbsOptions, selectFetchingBreadcrumbs, selectNodeCodetable,
  selectCodetableMetadata, selectFetchingMetadata,
  selectRequestedModel, selectListBranch, selectListBranchState,
  selectListActions, selectUpdating, selectActionErrors,
  selectCodetableValue, selectCategoryValue, selectActiveValue, selectActiveType, selectIncludeInactive,
  selectVisibility, selectSelectedRows, selectActiveValueError
} from './selectors';
import {
  ClearAll, AddListAction, CancelChanges, ClearList, ExpandListBranchItem, ClearActionErrors,
  UpdateCodetable, UpdateCategory, UpdateActiveValue, UpdateIncludeInactive, UpdateVisibility, SelectRow
} from './action-types';
import { LinkCodesActionType } from './constants';
import { ListAction, ListBranchModel, Visibility } from './types-store';
import { RowSelection } from './types';

export const useActionCreators = () => {

  const dispatch = useDispatch();
  return useMemo(() => bindActionCreators(actionCreators, dispatch), [dispatch]);
};

export const useBreadcrumbsSelectors = () => {

  const fetchingBreadcrumbs = useSelector(selectFetchingBreadcrumbs);
  const breadcrumbsOptions = useSelector(selectBreadcrumbsOptions);
  const nodeCodetable = useSelector(selectNodeCodetable);

  return {
    fetchingBreadcrumbs,
    breadcrumbsOptions,
    nodeCodetable
  };
};

export const useLoadBreadcrumbsEffect = (
  breadcrumbs: number[],
  linkSubset: boolean
) => {

  const dispatch = useDispatch();
  const { loadBreadcrumbs } = useActionCreators();

  const previousBreadcumbs = usePrevious(breadcrumbs);

  useEffect(
    () => {
      if (!breadcrumbs?.length || deepEquals(breadcrumbs ?? [], previousBreadcumbs ?? [])) {
        return;
      }

      dispatch(new ClearAll());
      loadBreadcrumbs(breadcrumbs, linkSubset);
    },
    [breadcrumbs, dispatch, loadBreadcrumbs]);
};

export const useLoadMetadataEffect = () => {

  const dispatch = useDispatch();
  const { loadMetadata } = useActionCreators();

  const { nodeCodetable } = useBreadcrumbsSelectors();
  const { activeType } = useDetailsSelectors();

  const previousNodeCodetable = usePrevious(nodeCodetable);
  const previousActiveType = usePrevious(activeType);

  useEffect(
    () => {
      if (!isDefinedIncludingZero(nodeCodetable) ||
        (nodeCodetable === previousNodeCodetable &&
          activeType === previousActiveType)
      ) {
        return;
      }

      dispatch(new UpdateCodetable({ value: nodeCodetable }));
      loadMetadata(nodeCodetable);
    },
    [nodeCodetable, previousNodeCodetable, activeType, previousActiveType, dispatch, loadMetadata]);
};

export const useMetadataSelectors = () => {

  const fetchingMetadata = useSelector(selectFetchingMetadata);
  const metadata = useSelector(selectCodetableMetadata);

  return {
    fetchingMetadata,
    metadata
  };
};

export const useDetailsSelectors = () => {

  const codetableValue = useSelector(selectCodetableValue);
  const categoryValue = useSelector(selectCategoryValue);
  const activeValue = useSelector(selectActiveValue);
  const activeType = useSelector(selectActiveType);
  const includeInactive = useSelector(selectIncludeInactive);

  return {
    codetableValue,
    categoryValue,
    activeValue,
    activeType,
    includeInactive
  };
};

export const useDetailsCallbacks = () => {

  const codetableValue = useSelector(selectCodetableValue);

  const dispatch = useDispatch();
  const { loadMetadata } = useActionCreators();

  const updateCodetable = useCallback(
    (value: ComplexValue<number>) => {
      if (codetableValue?.value !== value?.value) {
        dispatch(new UpdateCodetable(value));
        loadMetadata(value?.value);
      }
    },
    [codetableValue?.value, dispatch, loadMetadata]
  );

  const updateCategory = useCallback(
    (value: ComplexValue<number>) => {
      dispatch(new UpdateCategory(value));
    },
    [dispatch]
  );

  const updateActiveValue = useCallback(
    (value: ComplexValue<number>) => {
      dispatch(new UpdateActiveValue(value));
    },
    [dispatch]
  );

  const updateIncludeInactive = useCallback(
    (value: boolean) => {
      dispatch(new UpdateIncludeInactive(value));
    },
    [dispatch]
  );

  return {
    updateCodetable,
    updateCategory,
    updateActiveValue,
    updateIncludeInactive
  };
};

export const useLoadRootLevelListEffect = () => {

  const { codetableValue, categoryValue, activeType, includeInactive } = useDetailsSelectors();
  const { metadata, fetchingMetadata } = useMetadataSelectors();
  const requestedModel = useSelector(selectRequestedModel);
  const listBranch = useSelector(selectListBranch);

  const { loadListBranch } = useActionCreators();
  const dispatch = useDispatch();

  useEffect(
    () => {

      const model: ListBranchModel = {
        codetable: codetableValue?.value,
        category: categoryValue?.value,
        includeInactiveCodes: includeInactive,
        activeType
      };

      if (!compareModel(model, requestedModel)) {

        dispatch(new ClearList(model));
      }

      if (listBranch.length === 0 &&
        !fetchingMetadata &&
        isDefinedIncludingZero(codetableValue?.value) &&
        (!isDefinedIncludingZero(metadata?.category?.id) || isDefinedIncludingZero(categoryValue?.value))) {

        loadListBranch(model);
      }
    },
    [codetableValue, categoryValue, activeType, includeInactive, metadata, fetchingMetadata, listBranch, loadListBranch, dispatch]
  );
};

export const useListBranchSelectors = () => {

  const listBranch = useSelector(selectListBranch);
  const listBranchState = useSelector(selectListBranchState);

  return {
    listBranch,
    listBranchState
  };
};

export const useListBranchCallbacks = () => {

  const requestedModel = useSelector(selectRequestedModel);
  const { loadListBranch } = useActionCreators();
  const dispatch = useDispatch();

  const loadFromParent = useCallback(
    (id: number, startIndex: number) => {
      loadListBranch(requestedModel, id, startIndex);
    },
    [requestedModel, loadListBranch]
  );

  const expandListBranchItem = useCallback(
    (id: number) => {
      dispatch(new ExpandListBranchItem(id));
    },
    [dispatch]
  );

  return {
    loadFromParent,
    expandListBranchItem
  };
};

export const useListActionsSelectors = () => {

  const listActions = useSelector(selectListActions);
  const updating = useSelector(selectUpdating);
  const actionErrors = useSelector(selectActionErrors);
  const activeValueError = useSelector(selectActiveValueError);

  return {
    listActions,
    updating,
    actionErrors,
    activeValueError
  };
};

export const useListActionsCallbacks = () => {

  const { saveChanges: saveChangesAction } = useActionCreators();
  const requestedModel = useSelector(selectRequestedModel);
  const { activeValue, activeType } = useDetailsSelectors();
  const { listActions } = useListActionsSelectors();
  const newListActions: ListAction[] = listActions.map(item => {
    return isDefinedIncludingZero(requestedModel.category)
      ? { ...item, category: requestedModel.category }
      : item;
  });
  const dispatch = useDispatch();

  const addListAction = useCallback(
    (path: number[], type: LinkCodesActionType, propagate: boolean) => {
      dispatch(new AddListAction([{ path, type, propagate }]));
    },
    [dispatch]
  );

  const addListActions = useCallback(
    (paths: number[][], type: LinkCodesActionType, propagate: boolean) => {
      dispatch(new AddListAction(paths.map(path => ({ path, type, propagate }))));
    },
    [dispatch]
  );

  const saveChanges = useCallback(
    (successMessage: string, errorMessage: string) => {
      saveChangesAction(requestedModel, activeValue, activeType, newListActions, successMessage, errorMessage);
    }, [requestedModel, activeValue, activeType, newListActions, saveChangesAction]
  );

  const cancelChanges = useCallback(
    () => {
      dispatch(new CancelChanges());
    },
    [dispatch]
  );

  const clearActionErrors = useCallback(
    (fixListActions: boolean) => {
      dispatch(new ClearActionErrors(fixListActions));
    },
    [dispatch]
  );

  return {
    addListAction,
    addListActions,
    saveChanges,
    cancelChanges,
    clearActionErrors
  };
};

export const useListSelectors = () => {

  const visiblity = useSelector(selectVisibility);
  const selectedRows = useSelector(selectSelectedRows);

  return {
    visiblity,
    selectedRows
  };
};

export const useListCallbacks = () => {

  const dispatch = useDispatch();

  const updateVisibility = useCallback(
    (visibility: Visibility) => {
      dispatch(new UpdateVisibility(visibility));
    },
    [dispatch]
  );

  const selectRow = useCallback(
    (selection: RowSelection) => {
      dispatch(new SelectRow(selection));
    },
    [dispatch]
  );

  return {
    updateVisibility,
    selectRow
  };
};

export const usePromptOnChange = <T>(
  shouldPrompt: boolean,
  onChange: (value: T) => void
) => {

  const [displayPrompt, setDisplayPrompt] = useState(false);
  const [continueWithValue, setContinueWithValue] = useState<T>();

  const onPromptedChange = (value: T) => {
    if (shouldPrompt) {
      setDisplayPrompt(true);
      setContinueWithValue(value);
    } else {
      onChange(value);
    }
  };

  const onContinue = () => {
    onChange(continueWithValue);
  };

  const onHide = () => {
    setDisplayPrompt(false);
  };

  return { displayPrompt, onPromptedChange, onContinue, onHide };
};
